import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { type ReactNode } from 'react'
import { isEqual, omit } from 'lodash'
import { type Blocker } from 'react-router-dom'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { type ITabItem, toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import type {
  IParamsTestTrigger,
  IParamsTrigger,
  IResponseTrigger,
} from 'entities/Trigger/api/type'
import type { Phone } from 'entities/Phone/model/Phone'
import { TriggerApi } from 'entities/Trigger/api/trigger'
import { type Integration } from 'entities/Integrations/model/Integration'
import { IntegrationKey } from 'entities/Integrations'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { openRcsNotSupportedFilesModal } from 'entities/Rcs'
import { type IParamsSendFrom } from 'entities/SendFrom'
import {
  EnumModeTrigger,
  EnumTypeTrigger,
  type ITriggerCreateField,
  ITriggerCreateFieldEnum,
} from 'widgets/TriggerView/store/types'
import { TriggerSMSStore } from 'widgets/TriggerView/ui/TriggerSMS/store/TriggerSMSStore'
import { IntegrationDropdownStore } from 'widgets/IntegrationDropdown'
import { TrackConversionStore } from 'widgets/TrackConversions/store/trackConversionStore'
import { RinglessMessageFieldStore } from 'widgets/MessageFormFields/RinglessMessageField'
import { TagsDropdownControlStore } from 'widgets/TagsDropdown/store/tagsDropdownControlStore'
import { TriggerSendFromStore } from 'widgets/TriggerView/store/triggerSendFromStore'

export class TriggerViewStore {
  private _loading = true
  private _isSaving = false
  private _name = ''
  private _mode: EnumModeTrigger = EnumModeTrigger.Create

  private _responseEditTrigger: IResponseTrigger | null = null
  private _type: EnumTypeTrigger = EnumTypeTrigger.Sms
  private _isOpenAdvanced = false
  private _cacheParams: IParamsTrigger | null = null
  private _modalKeepEditing = nanoid()

  private readonly _triggerSMSStore: TriggerSMSStore
  private readonly _ringlessMessageFieldStore: RinglessMessageFieldStore
  private _errorsMap: Map<ITriggerCreateField, ReactNode> = new Map()
  private readonly _integrationDropdownStore: IntegrationDropdownStore
  private _triggerSendFromStore: TriggerSendFromStore
  private _trackConversionStore = new TrackConversionStore({
    clearError: () => {
      this.setError('conversion_id', '')
    },
  })
  private _tagsControlStore = new TagsDropdownControlStore()

  private _disposeIsAircallReaction: IReactionDisposer | null = null
  private _disposeIntegrationDropdownSelectedReaction: IReactionDisposer | null = null
  private _disposeTypeReaction: IReactionDisposer | null = null

  constructor(
    private _entities: IEntitiesStore,
    triggerResponse?: IResponseTrigger,
    isDuplicate?: boolean
  ) {
    makeAutoObservable(this)

    this._triggerSendFromStore = new TriggerSendFromStore(this._entities)
    this._triggerSMSStore = new TriggerSMSStore(
      {
        onFocus: () => {
          this.setError('smsMessage', '')
        },
        isMarketing: true,
      },
      this._entities
    )
    this._ringlessMessageFieldStore = new RinglessMessageFieldStore(this._entities, {
      clearParentError: () => {
        this.setError('ringlessMessage', '')
      },
    })
    this._integrationDropdownStore = new IntegrationDropdownStore(
      {
        clearIntegrationError: () => {
          this.setError('integration', '')
        },
        clearPhoneFieldError: () => {
          this.setError('phoneField', '')
        },
      },
      this._entities
    )
    void this.init(triggerResponse, isDuplicate)
  }

  init = async (triggerResponse?: IResponseTrigger, isDuplicate?: boolean) => {
    runInAction(() => {
      this._loading = true
    })
    await Promise.all([
      this._entities.integrationsStore.fetchIntegrations(),
      this._entities.mergeFieldStore.loadAllFields(),
    ])

    if (!triggerResponse) {
      void this._integrationDropdownStore.init({})
    }

    if (triggerResponse) {
      if (isDuplicate) {
        runInAction(() => {
          this._mode = EnumModeTrigger.Clone
          this._name = triggerResponse.name + ' (copy)'
        })
      } else {
        runInAction(() => {
          this._responseEditTrigger = triggerResponse
          this._mode = EnumModeTrigger.Edit
          this._name = triggerResponse.name
        })
      }

      if (triggerResponse.is_rvm) {
        runInAction(() => {
          this._type = EnumTypeTrigger.Ringless
        })
        const responseMediaShortUrl = triggerResponse?.media_url?.[0]
        if (responseMediaShortUrl) this._ringlessMessageFieldStore.init(responseMediaShortUrl)
      } else {
        this._triggerSMSStore.init(triggerResponse)
      }

      if (triggerResponse.tags?.length) {
        this._tagsControlStore.init(triggerResponse.tags)
      }

      if (triggerResponse.is_track_conversion) {
        this._isOpenAdvanced = true
      }
      void this._trackConversionStore.init({
        is_track_conversion: triggerResponse.is_track_conversion,
        conversion_id: triggerResponse.conversion_id,
      })

      await this._integrationDropdownStore.init({
        integration_id: triggerResponse.integration_id,
        integration: triggerResponse.integration,
        service_property: triggerResponse.service_property,
      })

      if (this._integrationDropdownStore.selectedIntegrationItem?.id) {
        await this.updateSendFromWithSelectedIntegration()
      }
      void this._triggerSendFromStore.sendFromStore.setSendFrom(triggerResponse.send_from)

      const isRCS = triggerResponse.send_from?.type === 'from_rcs_sender'

      if (isRCS) {
        this._type = EnumTypeTrigger.Rcs
        this._triggerSMSStore.messageFieldStore.setIsRCS(isRCS)
      }
    }

    if (!isDuplicate) {
      runInAction(() => {
        this._cacheParams = this.triggerParams
      })
    }

    this.reactionIsAircallInbox()
    this.reactionIntegrationSelected()
    this.reactionType()

    runInAction(() => {
      this._loading = false
    })
  }

  reset = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIntegrationDropdownSelectedReaction?.()
    this._disposeTypeReaction?.()
  }

  reactionType = () => {
    this._disposeTypeReaction?.()
    this._disposeTypeReaction = reaction(
      () => this._type,
      (status) => {
        this._triggerSendFromStore.sendFromStore.setSkipUnverifiedNumbers(
          status !== EnumTypeTrigger.Ringless
        )
        this._triggerSMSStore.messageFieldStore.setIsRCS(status === EnumTypeTrigger.Rcs)

        if (status === EnumTypeTrigger.Rcs) {
          this._triggerSMSStore.messageFieldStore.validateRcsAttachments()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionIsAircallInbox = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIsAircallReaction = reaction(
      () => this._triggerSendFromStore.sendFromStore.isAircall,
      (isAircall) => {
        this._triggerSMSStore.messageFieldStore.setIsCurrentAirCall(isAircall)
      }
    )
  }

  reactionIntegrationSelected = () => {
    this._disposeIntegrationDropdownSelectedReaction?.()
    this._disposeIntegrationDropdownSelectedReaction = reaction(
      () => this._integrationDropdownStore.selectedIntegrationItem?.id,
      () => {
        void this.updateSendFromWithSelectedIntegration()
      }
    )
  }

  updateSendFromWithSelectedIntegration = async () => {
    const selectedIntegration: Integration | undefined =
      this._integrationDropdownStore.selectedIntegrationItem?.data

    await this._triggerSendFromStore.updateIntegrationSenders(selectedIntegration)
  }

  private saveTrigger = async (triggerParams: IParamsTrigger): Promise<boolean> => {
    const cacheParams = this._cacheParams

    try {
      runInAction(() => {
        this._isSaving = true
        this._cacheParams = triggerParams
      })

      if (this._mode === EnumModeTrigger.Edit) {
        await TriggerApi.updateTrigger(triggerParams)
      } else {
        await TriggerApi.createTrigger(triggerParams)
      }
      return true
    } catch (e) {
      if (e instanceof AxiosError) {
        const error = e.errors.getError('error')

        if (error) {
          toastStore.add({
            type: 'error',
            title: error,
          })
        } else {
          const isTriggerField = (key: string): key is ITriggerCreateField =>
            key in ITriggerCreateFieldEnum
          e.errors.entries().forEach(([key, value]) => {
            if (isTriggerField(key)) {
              this.setError(key, value.value)
            } else {
              toastStore.add({
                type: 'error',
                title: value.value,
              })
            }
          })
        }
      }
      this._cacheParams = cacheParams
      console.log(e)
      return false
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  private _checkValidate = () => {
    const {
      name,
      is_rvm,
      media_url,
      message,
      integration_id,
      service_property,
      send_from,
      is_track_conversion,
      conversion_id,
    } = this.triggerParams

    if (!name.trim().length) {
      this.setError('name', 'Please enter trigger name')
    }

    if (is_rvm) {
      if (!media_url.length) {
        this.setError('ringlessMessage', 'Your message is the key – please select an audio')
      }
    } else {
      if (!message.trim().length && !media_url.length) {
        this.setError('smsMessage', 'Your message is the key – please write it above')
      }
    }

    if (integration_id && !(integration_id === 'custom') && !service_property) {
      this.setError('phoneField', 'Please select the phone field')
    }

    if (!send_from) {
      this.setError('sendFrom', 'Please select the inbox to send from')
    }

    if (is_track_conversion && !conversion_id) {
      this.setError('conversion_id', 'Please select or create a conversion to continue')
    }
  }

  handleCreateTrigger = async () => {
    this.clearErrors()
    this._checkValidate()

    if (this._errorsMap.size) {
      return
    }
    if ((this.type === 'sms' || this.type === 'rcs') && this._triggerSMSStore.isError) {
      return
    }

    const isSaveSuccessful = await this.saveTrigger(this.triggerParams)
    if (isSaveSuccessful) {
      uiStore.changeRoute({ path: '/triggers' })
    }
  }

  get triggerSendFromStore() {
    return this._triggerSendFromStore
  }

  get isExistChanges() {
    return !isEqual(omit(this._cacheParams, 'is_rvm'), omit(this.triggerParams, 'is_rvm'))
  }

  handleShowKeepEditingModal = (blocker: Blocker) => {
    if (blocker.state !== 'blocked') return

    modalStore.addModal({
      id: this._modalKeepEditing,
      type: ModalTypeList.WARNING,
      title: 'Leaving will discard your content',
      primaryAction: {
        text: 'Keep editing',
        onAction: () => {
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
      secondaryAction: {
        text: 'Discard',
        onAction: () => {
          const path = blocker.location.pathname
          blocker.proceed()

          uiStore.setRoutesBlocker(null)
          uiStore.changeRoute({
            path,
            type: 'vue',
            method: 'replace',
          })
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
    })
  }

  onTestSMSTrigger = (params: IParamsTestTrigger) => TriggerApi.createTest(params)

  selectTypeTab = (tab: ITabItem<EnumTypeTrigger>) => {
    const {
      messageFieldStore: { hasInvalidRcsAttachments },
    } = this._triggerSMSStore

    if (tab.key === 'rcs' && hasInvalidRcsAttachments) {
      openRcsNotSupportedFilesModal({
        onContinue: () => {
          this._type = tab.key
        },
      })

      return
    }

    this._type = tab.key
  }

  setName = (name: string) => {
    this._name = name
  }

  setOpenAdvanced = (value: boolean) => {
    this._isOpenAdvanced = value
  }

  clearErrors = () => {
    this._trackConversionStore.clearError()
    this._ringlessMessageFieldStore.clearError()
    this._triggerSendFromStore.sendFromStore.clearError?.()
    this._integrationDropdownStore.clearIntegrationError()
    this._integrationDropdownStore.clearPhoneFieldError()
    this._errorsMap.clear()
  }

  setError = (key: ITriggerCreateField, error: ReactNode) => {
    if (key === 'conversion_id') {
      this._trackConversionStore.setError(error)
    }
    if (key === 'ringlessMessage') {
      this._ringlessMessageFieldStore.setError(error)
    }
    if (key === 'sendFrom') {
      this._triggerSendFromStore.sendFromStore.setError(error as string)
    }
    if (key === 'integration') {
      this._integrationDropdownStore.setIntegrationError(error)
    }
    if (key === 'phoneField') {
      this._integrationDropdownStore.setPhoneFieldError(error)
    }
    if (!!error) this._errorsMap.set(key, error)
    else this._errorsMap.delete(key)
  }

  get tabs(): ITabItem<EnumTypeTrigger>[] {
    const tabs: ITabItem<EnumTypeTrigger>[] = [
      {
        key: EnumTypeTrigger.Sms,
        name: 'SMS',
      },
      {
        key: EnumTypeTrigger.Ringless,
        name: 'Ringless',
      },
    ]
    if (this._entities.inboxesStore.hasRcsInbox) {
      tabs.push({
        key: EnumTypeTrigger.Rcs,
        name: 'RCS',
        statusProps: {
          intent: 'warning',
          emphasis: 'high',
          title: 'Beta',
        },
      })
    }

    return tabs
  }

  get type() {
    return this._type
  }

  get rcsInbox() {
    return this._entities.inboxesStore.rcsInbox
  }

  get isOpenAdvanced() {
    return this._isOpenAdvanced
  }

  get isNumberOptional() {
    const senderType = this._triggerSendFromStore.sendFromStore.paramsSendFrom?.type
    const selectedIntegration = this._integrationDropdownStore.selectedIntegrationItem?.data

    return (
      senderType === 'from_integration' &&
      (selectedIntegration?.key === IntegrationKey.hubspot ||
        selectedIntegration?.key === IntegrationKey.infusionsoft)
    )
  }

  get sendFrom(): IParamsSendFrom | null {
    if (this.type === 'rcs') {
      return {
        type: 'from_rcs_sender',
        options: {
          team_id: this.rcsInbox?.id,
          number_id: null,
        },
      }
    }

    return this._triggerSendFromStore.sendFromStore.paramsSendFrom
  }

  get number(): Phone | undefined | null {
    const sendFrom = this._triggerSendFromStore.sendFromStore.paramsSendFrom
    if (!sendFrom) return

    if (sendFrom.options.number_id) {
      return this._entities.numbersStore.getItem(sendFrom?.options.number_id)
    }

    if (sendFrom.options.team_id) {
      const inbox = this._entities.inboxesStore.getItem(sendFrom.options.team_id)

      if (inbox && inbox.type === 'inbox') {
        const numberId = inbox?.sendingOptions?.find(
          (item) => item.key === sendFrom?.options?.smart_option
        )?.number_ids[0]

        return this._entities.numbersStore.getItem(numberId)
      }
    }
  }

  get messageTypeParams() {
    if (this.type === 'sms' || this.type === 'rcs') {
      return this._triggerSMSStore.messageDataParams
    }
    return this._ringlessMessageFieldStore.ringlessMessageDataParams
  }

  get triggerParams(): IParamsTrigger {
    return {
      id:
        this._mode === EnumModeTrigger.Edit && this._responseEditTrigger
          ? this._responseEditTrigger.id
          : null,
      name: this._name,
      is_rvm: this._type === EnumTypeTrigger.Ringless,

      send_from: this.sendFrom,

      is_track_conversion: this._trackConversionStore.is_track_conversion,
      conversion_id: this._trackConversionStore.conversion_id,

      tags: this._tagsControlStore.tags.map((tag) => tag.id),
      ...this._integrationDropdownStore.params,
      ...this.messageTypeParams,
    }
  }

  get loading() {
    return this._loading
  }

  get mode() {
    return this._mode
  }

  get ringlessMessageFieldStore() {
    return this._ringlessMessageFieldStore
  }

  get testSMSParams() {
    const isRingless = this._type === EnumTypeTrigger.Ringless
    const messageData = isRingless
      ? this._ringlessMessageFieldStore.ringlessMessageDataParams
      : this._triggerSMSStore.messageDataParams

    const params: IParamsTestTrigger = {
      media_url: messageData.media_url,
      message: messageData.message,
      is_rvm: isRingless,
      tags: this.tagsControlStore.tags.map((tag) => tag.id),
    }

    if (this.type === 'rcs') {
      params.team_id = this.rcsInbox?.id
    } else {
      params.number_id = this.number?.id || 0
    }

    return params
  }

  get integrationDropdownStore() {
    return this._integrationDropdownStore
  }

  get trackConversionStore() {
    return this._trackConversionStore
  }

  get tagsControlStore() {
    return this._tagsControlStore
  }

  get isSaving() {
    return this._isSaving
  }

  get triggerSMSStore() {
    return this._triggerSMSStore
  }

  get name() {
    return this._name
  }

  get errorsMap() {
    return this._errorsMap
  }
}
